/**
 * API interceptor utility for handling token refresh on API calls
 */

import { authService } from '../services/authService';
import { isTokenExpired } from './jwt';

interface ApiCallOptions extends RequestInit {
  retryCount?: number;
}

const MAX_RETRY_ATTEMPTS = 1;

/**
 * Enhanced fetch function that automatically handles token refresh
 * @param url API endpoint URL
 * @param options Fetch options including headers
 * @param tokens Current authentication tokens
 * @param onTokenRefresh Callback to update tokens after refresh
 * @returns Promise with API response
 */
export const apiCall = async (
  url: string,
  options: ApiCallOptions = {},
  tokens: { accessToken: string; refreshToken: string } | null,
  onTokenRefresh: (newTokens: { accessToken: string; refreshToken: string }) => void
): Promise<Response> => {
  const { retryCount = 0, ...fetchOptions } = options;

  // Add authorization header if tokens are available
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(fetchOptions.headers as Record<string, string>),
  };

  if (tokens?.accessToken) {
    headers['Authorization'] = `Bearer ${tokens.accessToken}`;
  }

  try {
    const response = await fetch(url, {
      ...fetchOptions,
      headers,
    });

    // If unauthorized and we have a refresh token, try to refresh
    if (response.status === 401 && tokens?.refreshToken && retryCount < MAX_RETRY_ATTEMPTS) {
      console.log('API call returned 401, attempting token refresh');
      
      try {
        const refreshResponse = await authService.refreshToken({
          refreshToken: tokens.refreshToken,
        });

        if (refreshResponse.succeeded && refreshResponse.data) {
          const newTokens = {
            accessToken: refreshResponse.data.accessToken,
            refreshToken: refreshResponse.data.refreshToken,
          };

          // Update tokens
          onTokenRefresh(newTokens);

          // Retry the original request with new token
          const newHeaders: Record<string, string> = {
            ...headers,
            'Authorization': `Bearer ${newTokens.accessToken}`,
          };

          return await fetch(url, {
            ...fetchOptions,
            headers: newHeaders,
          });
        } else {
          console.error('Token refresh failed:', refreshResponse.errorDescription);
          throw new Error('Token refresh failed');
        }
      } catch (refreshError) {
        console.error('Token refresh error:', refreshError);
        throw new Error('Token refresh failed');
      }
    }

    return response;
  } catch (error) {
    console.error('API call error:', error);
    throw error;
  }
};

/**
 * Check if a token is expired before making an API call
 * @param token Access token to check
 * @returns true if token is expired or will expire soon
 */
export const shouldRefreshToken = (token: string): boolean => {
  return isTokenExpired(token);
};
